import { useAtomValue } from 'jotai';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { OktaAuth } from '@okta/okta-auth-js'; // Correct import
import { LoginCallback, Security } from '@okta/okta-react';

import {
  ChatConnectionProvider,
  ConnectionProvider,
  FeatureFlag,
  FeatureFlagProvider,
  SocketProvider,
} from '@frontend/contexts';

import CanUser from './components/CanUser';
import CanUserWithFeatureFlag from './components/CanUserWithFeatureFlag';
import { ErrorBoundary } from './components/error-boundary';
import { OptiGlobalHeader } from './components/optiGlobalHeader';
import PathNavigator from './components/PathNavigator';
import RequireAuth from './components/RequireAuth'; // Custom auth wrapper for protected pages
import { WorkflowExecutionPage } from './features/agents/components/workflow-execution';
import { Chat } from './features/chat';
import { Instances } from './features/instances';
import { RAGConfiguration } from './features/rag';
import SettingsPage from './features/settings/Settings';
import { ToolsWarningBanner } from './features/tools-management/components/ToolsWarningBanner/ToolsWarningBanner';
import { InstructionPage } from './features/userInstructions/components';
import { BaseLayout } from './layouts/baseLayout';
import { SentryErrorBoundary } from './utils/sentry';
import { globalOptiHeaderVisibleAtom } from './atoms';
import { AgentPage, ToolsManagement } from './features';
import { useFontSize, useInstanceId, useTheme } from './hooks';
import oktaAuth from './oktaAuth';

function App() {
  return (
    <SentryErrorBoundary fallback={<p>An error has occurred</p>}>
      <WrappedSecurity />
    </SentryErrorBoundary>
  );
}

function WrappedSecurity() {
  const navigate = useNavigate();
  const { instanceId } = useInstanceId();
  const globalOptiHeaderVisible = useAtomValue(globalOptiHeaderVisibleAtom);
  useTheme();
  useFontSize();
  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri?: string): Promise<void> => {
    navigate(originalUri || '/');
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <Routes>
        <Route path="/login" element={<LoginCallback />} />
        <Route
          path="/instances/:instanceId"
          element={
            <RequireAuth>
              <FeatureFlagProvider>
                <ErrorBoundary>
                  <OptiGlobalHeader visible={globalOptiHeaderVisible} />
                </ErrorBoundary>
                <SocketProvider>
                  <ToolsWarningBanner />
                  <BaseLayout />
                </SocketProvider>
              </FeatureFlagProvider>
            </RequireAuth>
          }
        >
          <Route
            path="chat/*"
            element={
              <CanUser operation="allow_chat">
                <ChatConnectionProvider>
                  <Chat />
                </ChatConnectionProvider>
              </CanUser>
            }
          />
          <Route
            path="instructions/*"
            element={
              <CanUser operation="manage_instructions">
                <ConnectionProvider>
                  <InstructionPage />
                </ConnectionProvider>
              </CanUser>
            }
          />
          <Route
            path="connections/*"
            element={
              <CanUser operation="manage_instances">
                <Instances />
              </CanUser>
            }
          />
          <Route
            path="tools/*"
            element={
              <CanUser operation="allow_chat">
                <ToolsManagement />
              </CanUser>
            }
          />
          <Route
            path="settings/*"
            element={
              <CanUserWithFeatureFlag
                featureKey="appearance_settings"
                enabledPermission="allow_chat"
                disabledPermission="manage_instances"
              >
                <SettingsPage />
              </CanUserWithFeatureFlag>
            }
          />
          <Route
            path="agents/workflow/executions/:executionId/*"
            element={
              <CanUser operation="allow_chat">
                <WorkflowExecutionPage />
              </CanUser>
            }
          />
          <Route
            path="agents/*"
            element={
              <CanUser operation="manage_instances">
                <AgentPage />
              </CanUser>
            }
          />
          <Route
            path="rag/*"
            element={
              <FeatureFlag featureKey="shamwow-settings-page">
                <RAGConfiguration />
              </FeatureFlag>
            }
          />
          <Route path="" element={<PathNavigator path={`/instances/${instanceId}/chat`} />} />
        </Route>
        <Route
          path="*"
          element={
            <RequireAuth>
              <PathNavigator path={`/instances/${instanceId}/chat`} />
            </RequireAuth>
          }
        />
      </Routes>
    </Security>
  );
}

export default App;
